import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_ENDPOINTS from '../../../Api/endpoints';
import { mockCostAnalysis } from '../../../mockData/paymentGateways';

const GatewayCostAnalysis = () => {
  const auth = useSelector((state) => state.auth);
  const token = auth?.user?.token || auth?.token || '';

  const [analysis, setAnalysis] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, []);

  const loadAnalysis = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual API call in Backend Phase
      // const response = await axios.get(API_ENDPOINTS.PAYMENT_GATEWAYS.COST_ANALYSIS, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      // setAnalysis(response.data);

      // For now, use mock data
      setAnalysis(mockCostAnalysis);
    } catch (err) {
      console.error('Failed to load cost analysis', err);
    } finally {
      setLoading(false);
    }
  };

  const totalVolume = analysis.reduce((sum, item) => sum + item.totalVolume, 0);
  const totalFees = analysis.reduce((sum, item) => sum + item.totalFees, 0);
  const totalTransactions = analysis.reduce((sum, item) => sum + item.totalTransactions, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-AE').format(num);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Payment Gateway Cost Analysis
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Volume (All Gateways)
              </Typography>
              <Typography variant="h4">
                {formatCurrency(totalVolume)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Fees Paid
              </Typography>
              <Typography variant="h4" color="error.main">
                {formatCurrency(totalFees)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {((totalFees / totalVolume) * 100).toFixed(2)}% of volume
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h4">
                {formatNumber(totalTransactions)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg: {formatCurrency(totalVolume / totalTransactions)} per transaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analysis Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Gateway Comparison
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Gateway</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Total Volume</TableCell>
                  <TableCell align="right">Total Fees</TableCell>
                  <TableCell align="right">Avg Fee %</TableCell>
                  <TableCell align="right">Last Month Volume</TableCell>
                  <TableCell align="right">Last Month Fees</TableCell>
                  <TableCell align="center">Trend</TableCell>
                  <TableCell align="right">Market Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysis.map((item) => {
                  const marketShare = (item.totalVolume / totalVolume) * 100;
                  const volumeChange = item.totalVolume - item.lastMonthVolume;
                  const volumeChangePercent = (volumeChange / item.lastMonthVolume) * 100;

                  return (
                    <TableRow key={item.gatewayName}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {item.gatewayName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(item.totalTransactions)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.totalVolume)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main">
                          {formatCurrency(item.totalFees)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {item.avgFeePercentage.toFixed(2)}%
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.lastMonthVolume)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.lastMonthFees)}
                      </TableCell>
                      <TableCell align="center">
                        {item.trend === 'up' ? (
                          <Chip
                            icon={<TrendingUpIcon />}
                            label={`+${volumeChangePercent.toFixed(1)}%`}
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<TrendingDownIcon />}
                            label={`${volumeChangePercent.toFixed(1)}%`}
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flexGrow: 1, mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={marketShare}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2">
                            {marketShare.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Cost Optimization Insight */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="h6" color="info.main" gutterBottom>
              Cost Optimization Insight
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bank Transfer has 0% fees - Encouraging customers to use bank transfers for large amounts can reduce costs.
              {analysis.find(a => a.gatewayName === 'Noqodi') && (
                <> Noqodi has the lowest card/wallet fee at {analysis.find(a => a.gatewayName === 'Noqodi').avgFeePercentage}%.</>
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GatewayCostAnalysis;
